import { useEffect, useState } from 'react';

export interface Zugangsdaten {
  email: string | null;
  passwort: string;
  /** true = Konto neu angelegt, false = Passwort für ein bestehendes ersetzt. */
  neu: boolean;
}

/**
 * Zugangsdaten nach dem Anlegen oder Zurücksetzen — als Dialog.
 *
 * Vorher standen sie in einem Block über der Benutzerliste, und die Seite
 * sprang beim Erzeugen dorthin. Wer unten in der Liste auf „Passwort neu"
 * klickte, sah die Seite nach oben springen und hielt das für „nichts
 * passiert" — das Passwort lag längst da, nur außerhalb des Blicks.
 *
 * Ein Dialog kann man nicht übersehen und nicht wegscrollen. Er passt auch
 * zur Sache: das Passwort ist genau einmal zu sehen, danach nie wieder, also
 * soll es aktiv weggeklickt werden statt beim Weiterscrollen zu verschwinden.
 */
export function ZugangSheet({
  zugang,
  onClose,
}: {
  zugang: Zugangsdaten | null;
  onClose: () => void;
}) {
  const [kopiert, setKopiert] = useState<'email' | 'passwort' | 'beides' | null>(null);

  useEffect(() => {
    if (!zugang) return;
    setKopiert(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zugang, onClose]);

  if (!zugang) return null;

  async function kopieren(text: string, was: 'email' | 'passwort' | 'beides') {
    try {
      await navigator.clipboard.writeText(text);
      setKopiert(was);
    } catch {
      // Ohne Zwischenablage-Rechte bleibt das Markieren von Hand — die Felder
      // sind auswählbar, deshalb ist das kein Fehler, den man melden müsste.
      setKopiert(null);
    }
  }

  const beides = `${zugang.email ?? ''}\n${zugang.passwort}`;

  return (
    <div
      className="sheet"
      role="dialog"
      aria-modal="true"
      aria-label="Zugangsdaten"
      onClick={onClose}
    >
      <div className="sheet__panel" onClick={(event) => event.stopPropagation()}>
        <div className="sheet__head">
          <h2>// zugangsdaten</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Schließen">
            ✕
          </button>
        </div>

        <p>
          {zugang.neu
            ? `Das Konto ist angelegt. Diese Daten stehen nur jetzt hier — gib sie weiter, bevor du schliesst.`
            : `Das Passwort wurde ersetzt. Das alte gilt ab sofort nicht mehr.`}
        </p>

        <label className="field" style={{ marginTop: '.75rem' }}>
          <span className="field__label">E-Mail</span>
          <input
            className="input"
            readOnly
            value={zugang.email ?? ''}
            onFocus={(e) => e.currentTarget.select()}
          />
        </label>

        <label className="field" style={{ marginTop: '.5rem' }}>
          <span className="field__label">Passwort</span>
          <input
            className="input mono"
            readOnly
            value={zugang.passwort}
            onFocus={(e) => e.currentTarget.select()}
          />
        </label>

        <div className="form__actions" style={{ marginTop: '.75rem' }}>
          <button
            type="button"
            className="px-btn px-btn--sm"
            onClick={() => void kopieren(beides, 'beides')}
          >
            {kopiert === 'beides' ? 'Kopiert' : 'Beides kopieren'}
          </button>
          <button
            type="button"
            className="px-btn px-btn--ghost px-btn--sm"
            onClick={() => void kopieren(zugang.passwort, 'passwort')}
          >
            {kopiert === 'passwort' ? 'Kopiert' : 'Nur Passwort'}
          </button>
          <button type="button" className="px-btn px-btn--ghost px-btn--sm" onClick={onClose}>
            Schliessen
          </button>
        </div>

        <p className="card__meta" style={{ marginTop: '.75rem' }}>
          Bitte auf einem Weg weitergeben, auf dem es nicht stehen bleibt — und die Person
          bitten, es nach dem ersten Login zu aendern.
        </p>
      </div>
    </div>
  );
}
